import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import {
  NgxPermissionsService,
  NgxRolesService,
  ValidationFn,
} from 'ngx-permissions';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { decodeToken, isTokenExpired } from '../util/jwtToken';

import {
  catchError,
  concatMap,
  distinctUntilChanged,
  finalize,
  map,
  share,
  take,
  tap,
} from 'rxjs/operators';
import { AuthConfig } from '../interface';
import { APP_ADMIN_PERMISSIONS, APP_PERMISSIONS, OAUTH_CONFIG } from '../token';

import { API_BASE } from '@core/api';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';

/**
 * Local-storage keys the **web** wipe keeps (see {@link AuthService.logout}).
 * They belong to the PC rather than to a session, so they must outlive a
 * logout and a failed refresh. `fe-web`'s photo-station memory service writes
 * them and imports the names from here, because `core_auth` may not import from
 * an app. Keep this list short: every key on it survives every user's logout
 * on a shared PC.
 */
export const WEB_PRESERVED_STORAGE_KEYS = {
  /** The browser id that the workstation lock identity is hashed from. */
  browserId: 'browser_id',
  /** The workstation last selected in this browser. */
  lastWorkstation: 'photo_station_last_workstation',
} as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private isAuthenticated = new BehaviorSubject<boolean>(false);

  private validateToken = new Subject<void>();
  private validateAccessCode = new Subject<void>();
  private validToken$: Observable<string | null> = this.validateToken.pipe(
    map(() => [
      this.storage.getItem('access_token'),
      this.storage.getItem('refresh_token'),
    ]),
    concatMap(([access_token, refresh_token]) => {
      if (access_token !== null && !isTokenExpired(access_token)) {
        return of(access_token);
      } else if (refresh_token !== null && this.http) {
        return this.http
          .post(`${this.apiBase}/oauth/token`, {
            grant_type: 'refresh_token',
            refresh_token,
            client_id: this.oauth.client_id,
            client_secret: this.oauth.client_secret,
            scope: '',
          })
          .pipe(
            map((data: any) => {
              this.storage.setItem('access_token', data.access_token);
              this.storage.setItem('refresh_token', data.refresh_token);
              return data.access_token;
            }),
            catchError((e) => {
              // A failed refresh is a dead session, so the credentials go. What
              // must NOT go is `mobile_session` (see {@link updateSession}):
              // its absence means "nobody has ever completed a hand-over here"
              // and opens the device gate, so destroying it would let an
              // appraiser walk past undelivered work.
              //
              // No caller can opt out of this branch — the pipeline is
              // subscribed in the constructor, so it runs on any app start with
              // an expired access token and a refresh that fails, i.e. simply
              // being offline. That is exactly the reopen this gate exists for.
              //
              // Hence the split rather than a bare `clear()`. Presence of
              // `mobile_session` stands in for "this is the mobile app": on web
              // the key is never written, and web needs the blanket wipe because
              // this service cannot enumerate what other features stored. The
              // proxy is inferential and only holds while `AuthService` is the
              // sole writer to this store — see {@link logout}.
              if (this.storage.getItem('mobile_session') === null) {
                this.clearWebStorage();
              } else {
                this.clearMobileStorage();
              }

              this.isAuthenticated.next(false);
              return of('session-expired');
            })
          );
      } else {
        this.isAuthenticated.next(false);
        return of(null);
      }
    }),
    share()
  );

  private validAccessCode$ = this.validateAccessCode.pipe(
    map(() => [this.storage.getItem('access_code')]),
    concatMap(([access_code]) => {
      if (access_code !== null) {
        return of(access_code);
      } else {
        return of(null);
      }
    }),
    share()
  );

  get token$(): Observable<string | null> {
    return new Observable((subscriber) => {
      this.validToken$.pipe(take(1)).subscribe((x) => {
        subscriber.next(x);
        subscriber.complete();
      });
      this.validateToken.next();
    });
  }

  get accessCode$(): Observable<string | null> {
    return new Observable((subscriber) => {
      this.validAccessCode$.pipe(take(1)).subscribe((x) => {
        subscriber.next(x);
        subscriber.complete();
      });
      this.validateAccessCode.next();
    });
  }

  get isAuthenticated$() {
    return this.isAuthenticated.pipe(distinctUntilChanged());
  }

  get userId(): number | null {
    const userId = this.storage.getItem('user_id');
    return userId !== null && !isNaN(+userId) ? +userId : null;
  }

  constructor(
    @Inject(WA_LOCAL_STORAGE) readonly storage: Storage,
    @Inject(API_BASE) readonly apiBase: string,
    @Inject(OAUTH_CONFIG) readonly oauth: AuthConfig,
    @Optional() @Inject(APP_PERMISSIONS) readonly permissions: string[],
    @Optional()
    @Inject(APP_ADMIN_PERMISSIONS)
    readonly adminPermissions: string[],
    private http: HttpClient,
    private permissionsService: NgxPermissionsService,
    private rolesService: NgxRolesService
  ) {
    if (permissions instanceof Array) {
      this.permissionsService.loadPermissions(
        permissions,
        this.checkPermission as ValidationFn
      );
    }
    // this.rolesService.addRoles(roles);
    // this.isAuthenticated.next(false);

    this.token$.subscribe((token) => this.isAuthenticated.next(!!token));
    this.accessCode$.subscribe((accessCode) => {
      if (accessCode) {
        this.isAuthenticated.next(!!accessCode);
      }
    });
  }

  /**
   * Get an access token
   * @param username The email address
   * @param password The password string
   */
  login(username: string, password: string, redirect?: (string | number)[]) {
    return this.http
      .post<{
        access_token: string;
        refresh_token: string;
        roles: string[];
        user_id: number;
        customer: unknown | null;
      }>(`${this.apiBase}/oauth/token`, {
        grant_type: 'password',
        client_id: this.oauth.client_id,
        client_secret: this.oauth.client_secret,
        username,
        password,
        scope: '',
      })
      .pipe(
        tap((data) => {
          const decodedToken = decodeToken(data.access_token);
          this.storage.setItem('access_token', data.access_token);
          this.storage.setItem('refresh_token', data.refresh_token);
          if (decodedToken.user_id) {
            this.storage.setItem('user_id', decodedToken.user_id);
          }
          this.storage.setItem('customer', JSON.stringify(data.customer));
          this.storage.setItem('session_timestamp', `${Date.now()}`);
          this.isAuthenticated.next(true);
        })
      );
  }

  /**
   * Revoke the authenticated user token.
   *
   * **Web only. Never call this from the mobile app.**
   *
   * `storage.clear()` means different things per platform, and this service
   * cannot see which one it got. On web, `WA_LOCAL_STORAGE` is the browser's
   * `localStorage` and `clear()` drops its keys. On mobile it is
   * `MobileStorage`, whose `clear()` calls `destroyDatabase()` — it deletes the
   * whole `mobile-local-storage` Couchbase database from disk. Use
   * {@link logoutApp} on mobile.
   *
   * On web the wipe keeps the keys in {@link WEB_PRESERVED_STORAGE_KEYS}: the
   * browser id and the last selected photo workstation (RIEG-274). Both belong
   * to the PC, not to whoever is logged in, so the next login on the same PC
   * must find them. Everything else still goes. See {@link clearWebStorage}.
   *
   * `mobile_session` is the one key that must outlive a logout, because its
   * absence opens the device gate (see {@link updateSession}). `logoutApp()`
   * preserves it by omission from {@link clearMobileStorage}; `clear()` would
   * destroy it. That is the whole reason the refresh-failure branch above picks
   * between the two rather than always clearing.
   *
   * Beyond that one key the two methods are equivalent today, because this
   * service is the only writer to the store — they remove the same set. The
   * web-only rule is forward-looking: as soon as a second feature persists
   * anything here the two diverge, `clear()` destroying the new key and
   * `logoutApp()` silently missing it, and which of those is worse depends
   * entirely on the key.
   *
   * That split is the actual defect, and this comment is not a fix for it. Both
   * semantics are wanted: web must wipe blanket, because this service cannot
   * enumerate what other features stored; mobile must not. So the fix is not to
   * make `clear()` gentler — a per-document delete loop would trade one atomic
   * filesystem operation for N interruptible ones and leave a Couchbase
   * tombstone per key per cycle. The fix is to give auth its own store, injected
   * per platform, so `clear()` keeps one meaning and destroys only what auth
   * owns. Adding a second, hand-maintained logout method moved the problem into
   * every caller instead: `logoutApp()` enumerates six keys by hand, so any key
   * added later is silently missed on mobile and silently destroyed on web.
   * Scope the store, then delete one of these two methods — do not add a third.
   */
  public logout() {
    return this.http.get(`${this.apiBase}/user/logout`).pipe(
      finalize(() => {
        this.clearWebStorage();
        this.isAuthenticated.next(false);
      })
    );
  }

  /**
   * The web wipe: `clear()`, then put back the keys in
   * {@link WEB_PRESERVED_STORAGE_KEYS}. Clearing and restoring keeps the
   * blanket semantics — anything a feature stored that is not on the list
   * still goes — rather than turning it into a hand-maintained delete list.
   * Web only: on mobile `clear()` destroys the database (see {@link logout}).
   */
  private clearWebStorage() {
    const kept = Object.values(WEB_PRESERVED_STORAGE_KEYS)
      .map((key) => [key, this.storage.getItem(key)] as const)
      .filter(([, value]) => value !== null);
    this.storage.clear();
    for (const [key, value] of kept) {
      this.storage.setItem(key, value as string);
    }
  }

  /**
   * Mobile-safe logout: removes the auth keys individually instead of clearing
   * the store, because on mobile `clear()` destroys the whole database. See
   * {@link logout} for why this method exists and why it should not.
   *
   * The one behavioural difference from `clear()` is that `mobile_session`
   * survives — see {@link clearMobileStorage}. The costs are real though: each
   * `removeItem` leaves a Couchbase deletion tombstone, so the database file
   * grows per logout cycle where `destroyDatabase()` would reset it, and the
   * removals are interruptible, admitting a partial state (`access_token` gone,
   * `refresh_token` kept) that lets the refresh pipeline re-establish the very
   * session this call was meant to end.
   */
  public logoutApp() {
    return this.http.get(`${this.apiBase}/user/logout`).pipe(
      finalize(() => {
        this.clearMobileStorage();
        this.isAuthenticated.next(false);
      })
    );
  }

  /**
   * Removes the credentials for the current session, key by key.
   *
   * `session_timestamp` goes with them: it identifies *this login*, so the next
   * login must mint a fresh one (see {@link updateSession}).
   *
   * `mobile_session` is deliberately absent from this list and must stay absent.
   * It records which login last completed a hand-over and has to outlive a
   * logout — removing it would open the device gate on a device that may still
   * hold undelivered work.
   *
   * The list is hand-maintained. Anything added to storage elsewhere has to be
   * added here too, or it outlives the session.
   */
  private clearMobileStorage() {
    if (this.storage.getItem('access_token') !== null) {
      this.storage.removeItem('access_token');
    }
    if (this.storage.getItem('refresh_token') !== null) {
      this.storage.removeItem('refresh_token');
    }
    if (this.storage.getItem('permissions') !== null) {
      this.storage.removeItem('permissions');
    }
    if (this.storage.getItem('user_id') !== null) {
      this.storage.removeItem('user_id');
    }
    if (this.storage.getItem('customer') !== null) {
      this.storage.removeItem('customer');
    }
    if (this.storage.getItem('access_code') !== null) {
      this.storage.removeItem('access_code');
    }
    if (this.storage.getItem('session_timestamp') !== null) {
      this.storage.removeItem('session_timestamp');
    }
  }

  public forgotPassword(email: string) {
    return this.http.post<{
      message: string;
      status: number;
      data: null | unknown[];
    }>(`${this.apiBase}/api/component/users/forgotPassword`, { email });
  }

  public resetPassword(
    hash: string,
    password: string,
    passwordConfirmation: string
  ) {
    return this.http.post<{
      message: string;
      status: number;
      data: null | unknown[];
    }>(`${this.apiBase}/api/component/users/resetPassword`, {
      hash,
      pw1: password,
      pw2: passwordConfirmation,
    });
  }

  public changePassword(
    oldPassword: string,
    password: string,
    passwordConfirmation: string
  ) {
    return this.http.post<{
      message: string;
      status: number;
      data: null | unknown[];
    }>(`${this.apiBase}/api/component/users/changePassword`, {
      oldPassword,
      pw1: password,
      pw2: passwordConfirmation,
    });
  }

  public hasPermission = (...permissions: string[]) => {
    const userPermissions = JSON.parse(
      this.storage.getItem('permissions') || '[]'
    );
    return userPermissions instanceof Array
      ? permissions.every((permission) => userPermissions.includes(permission))
      : false;
  };

  public checkPermission = (...permissions: string[]) => {
    const userPermissions = JSON.parse(
      this.storage.getItem('permissions') || '[]'
    );

    return userPermissions instanceof Array
      ? permissions.some(
          (permission) =>
            userPermissions.some((userPermission) =>
              this.adminPermissions.includes(userPermission)
            ) || userPermissions.includes(permission)
        )
      : false;
  };

  public loginWithAccesCode(code: string) {
    this.storage.setItem('access_code', code);
    this.storage.setItem('permissions', '');
    this.storage.setItem('user_id', '');
    this.storage.setItem('customer', '');
    this.storage.setItem('session_timestamp', `${Date.now()}`);

    this.isAuthenticated.next(true);
    return of(code);
  }

  /**
   * Device hand-over gate. `updateSession`, `validateSession` and
   * `buildSessionIdentifier` are **mobile-only** — nothing enforces that, so
   * treat it as a rule.
   *
   * The problem: a mobile device passes between appraisers, and a login uploads
   * and clears the previous appraiser's work before the next one may start. If
   * that hand-over does not complete, nobody may enter the app — otherwise the
   * incoming appraiser works against a stale project list, and their captures
   * mix with retained work that nothing in the data identifies an owner for
   * (`mark_synced` records whether an item was delivered, never whose it is).
   *
   * The mechanism is one record compared against one derived value:
   *
   * - `session_timestamp` is minted on every login, by both {@link login} and
   *   {@link loginWithAccesCode}, and removed on logout.
   * - `buildSessionIdentifier` combines the current user (or access code) with
   *   that timestamp, so it names **a login**, not a person.
   * - `mobile_session` stores that identifier, written by `updateSession` only
   *   after a hand-over has completed, and never removed.
   * - `validateSession` therefore answers: did *this login* complete the last
   *   hand-over? Absence of the record passes **only when the device's local
   *   inventory collection is empty too** — a first hand-over that stopped
   *   leaves retained items and no record at all, and absence alone would wave
   *   it through. The caller supplies that emptiness fact; see
   *   {@link validateSession}.
   *
   * Identifying the login rather than the user is what makes it correct. A
   * different appraiser mismatches on the user part; the *same* appraiser
   * logging in again mismatches on the timestamp, so an abandoned run cannot
   * masquerade as a completed one. It also means the gate never depended on
   * telling people apart — which matters, because `loginWithAccesCode` performs
   * no server-side validation and an access code names a project rather than a
   * person.
   *
   * Because the timestamp is minted at login, before any hand-over can run, the
   * previous record is invalidated by construction — no separate write-ahead
   * flag and no ordering discipline to get wrong. A process killed at any point
   * between login and completion leaves a mismatch, so the device stays gated.
   *
   * **Do not fold `access_token` into the identifier.** It is read as a
   * null-guard only. The refresh pipeline rewrites it mid-session, so making it
   * part of the value would invalidate a perfectly healthy session and gate
   * spuriously.
   *
   * Failure direction is safe: if the app dies between a hand-over completing
   * and `updateSession` running, the device reads as gated though it is clean.
   * The retry finds nothing to upload, succeeds, and releases the gate.
   *
   * ---
   *
   * `updateSession` itself: record the current login as the one that completed
   * a hand-over.
   *
   * **Call this on a fully delivered hand-over only** (RIEG-262 KTD26). Nothing
   * is written on a failed or abandoned run, and nothing needs to be: the next
   * login mints a fresh `session_timestamp`, which invalidates the previous
   * record by construction.
   *
   * **It refuses to write an unusable identifier**, and the refusal is the load
   * bearing part:
   *
   * - an **empty identifier** (no token/user *and* no access code) stored here
   *   would be matched by {@link validateSession} against every later login
   *   that is itself unnameable, opening the gate permanently;
   * - a **missing `session_timestamp`** yields `user_42_null`, which names no
   *   login at all.
   *
   * Both are reachable *mid-run*, not just in theory: the upload path reads the
   * token per item, so a session expiring during a long hand-over sends the
   * refresh pipeline into {@link clearMobileStorage}, which removes `user_id`,
   * `access_code` and `session_timestamp` while the run carries on.
   *
   * @returns whether the record was written. A `false` is an **abnormal
   *   hand-over end** — the caller must log it and leave the device gated, not
   *   retry the write.
   */
  updateSession(): boolean {
    const identifier = this.buildSessionIdentifier();

    if (identifier === '' || this.storage.getItem('session_timestamp') === null) {
      // Unconditional, like the upload stamps: this is the one trace a run that
      // lost its session mid-hand-over leaves behind.
      console.error(
        '[hand-over] updateSession refused to record the session: the current ' +
          'login has no usable identifier (session lost mid-run?). The device ' +
          'stays gated.'
      );
      return false;
    }

    this.storage.setItem('mobile_session', identifier);
    return true;
  }

  /**
   * Record that a hand-over delivered everything but did **not** finish
   * (RIEG-262, residual finding on KTD26).
   *
   * The gap this closes: `updateSession` runs only after
   * `resetLocalDataToServerProjects` resolves. A reset that fails therefore
   * writes nothing — while every item has already been delivered and deleted,
   * so the collection is now empty. On a device whose *first ever* hand-over
   * this was, {@link validateSession} then reads *record absent + collection
   * empty* and lets the next start straight in, on the previous appraiser's
   * project list, which is exactly what the failed reset did not refresh.
   *
   * KTD26 rejected a **write-ahead** flag, and this is not one: nothing is
   * written before the reset, so a process killed mid-run still leaves the
   * device gated by the timestamp mismatch, which is what that decision
   * protects. This writes *after* a known failure, converting "no record" —
   * which reads as "nobody has ever worked here" — into "a hand-over ended
   * badly here", which is the truth.
   *
   * The sentinel is a value {@link buildSessionIdentifier} cannot produce: it
   * builds `user_…` and `code_…` only, so no login can ever match this and the
   * device stays gated until a retry writes a real record. It carries the
   * timestamp of the failure rather than being a bare constant, so a device
   * found in this state can be placed in time.
   *
   * Unconditional, unlike {@link updateSession}: there is no identifier to be
   * unusable, and a session lost mid-run is one of the ways to arrive here.
   */
  invalidateSession(failedAt: number): void {
    this.storage.setItem('mobile_session', `reset-failed_${failedAt}`);
    console.error(
      '[hand-over] every item was delivered but the project reset failed, so ' +
        'the device was not refreshed. It stays gated until a retry completes.'
    );
  }

  /**
   * The gate's *read*: may the current login enter the app?
   *
   * Half of the answer lives here, because `session_timestamp` and the
   * identifier are built here: **does the current login's identifier match the
   * stored record.** The other half — whether the device still holds local
   * inventory items — is a Couchbase query that this library cannot make.
   * `core_auth` is cross-platform (`fe-web` imports it), so it may not reach
   * `@mobile/storage`, and the read is asynchronous while this method is not.
   * The mobile guard performs it and passes the fact in.
   *
   * Composed, the rule is: **entry passes when the identifier matches the
   * record, or when the record is absent *and* the collection is empty.**
   *
   * Absence alone must not pass. The record is written on success only, so a
   * device whose *first* hand-over stopped holds retained items and no record
   * at all — the one case a bare absence-passes rule waves straight through.
   * Requiring an empty collection alongside is what still leaves a device
   * nobody has ever worked on ungated.
   *
   * @param localCollectionEmpty whether the local inventory collection holds
   *   nothing. **Required, and deliberately not defaulted.** A default of
   *   `true` would compile everywhere, keep every existing test green, and
   *   quietly reinstate the bypass; the workspace lint cannot catch it either,
   *   since `core_auth` and `mobile_storage` are both `type:util` and no
   *   `platform:` rule applies. A required argument turns the omission into a
   *   compile error instead.
   */
  validateSession(localCollectionEmpty: boolean): boolean {
    const mobile_session = this.storage.getItem('mobile_session');

    if (mobile_session === null) {
      return localCollectionEmpty;
    }

    return this.buildSessionIdentifier() === mobile_session;
  }

  /**
   * Name the current login: its **kind** plus the timestamp minted for it.
   *
   * **The access-code form must never contain the code itself.** The value goes
   * into `mobile_session`, which deliberately survives logout and the
   * refresh-failure wipe — so a code written here outlives the session it
   * belongs to and no app action can remove it, on a device that passes between
   * appraisers. The API accepts an access code for six months from its creation
   * date (`AccessCodeValidation::isAccessCodeValid`), with no revocation in
   * practice, so that is a working credential left behind.
   *
   * The timestamp alone identifies a login uniquely, which is the whole
   * requirement: the gate compares logins, never people. The `user_` / `code_`
   * prefix stays so the two kinds cannot collide on a shared timestamp.
   */
  private buildSessionIdentifier() {
    const token = this.storage.getItem('access_token');
    const user = this.storage.getItem('user_id');
    const code = this.storage.getItem('access_code');
    const timestamp = this.storage.getItem('session_timestamp');
    return token !== null && user !== null
      ? `user_${user}_${timestamp}`
      : code !== null
      ? `code_${timestamp}`
      : '';
  }
}
